import { HttpError } from '@littlebird/auth-client';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';

import { backendApis } from '@/backendApis';
import { readCheckoutReturnChannel } from '@/features/channel-dashboard/channel-via';
import { IPCEventType } from '@/features/electron-api/base/types';
import { meetingStore } from '@/features/meetings/meetings.state';
import { queryClient } from '@/query/client';
import { inElectron } from '@/utils/capabilities';
import { handleExternalUrlClick } from '@/utils/platform';

import { subscriptionStore } from './subscription.store';
import {
  type CheckoutClient,
  type CreateCheckoutSessionRequest,
  type PurchaseCreditPackRequest,
  type UpdateAutoRefillRequest,
  type UpgradeToPowerRequest,
  type UpgradeToProRequest,
} from './subscription.types';
import { creditsToValueCents, formatCentsExact } from './usage-format';

function isStripeRedirectBlocked(): boolean {
  if (meetingStore.recording.status === 'recording') {
    toast.error(
      'Cannot open Stripe during an active meeting. Please stop transcribing first.'
    );
    return true;
  }
  return false;
}

export const checkoutClient = (): CheckoutClient =>
  inElectron() ? 'desktop' : 'web';

function openStripe(url: string, webMessage: string, desktopMessage: string) {
  if (inElectron()) {
    toast.success(desktopMessage);
    handleExternalUrlClick(url);
  } else {
    toast.success(webMessage);
    window.location.href = url;
  }
}

function syncSubscriptionPlanToContextKit(subscriptionPlan: string | null) {
  if (!inElectron()) return;

  try {
    window.mainProcessApi.notifyContextKit(IPCEventType.setSubscriptionPlan, {
      subscriptionPlan,
    });
  } catch (error) {
    console.error('Failed to sync subscription plan to ContextKit:', error);
  }
}

export const subscriptionApis = {
  /** Fetches subscription status from the backend API and updates the store. */
  async getStatus() {
    subscriptionStore.updateState({
      isLoading: true,
      error: null,
    });
    try {
      const status = await backendApis.subscription.getStatus();
      subscriptionStore.updateState({
        currentPlan: status.current_plan,
        mcpServerPermitted: status.mcp_server_permitted,
        currentPlanDisplayName: status.current_plan_display_name ?? null,
        planChangeDate: status.plan_change_date,
        stripeCustomerId: status.stripe_customer_id,
        cancelAtPeriodEnd: status.cancel_at_period_end,
        subscriptionStatus: status.subscription_status,
        currentPeriodEnd: status.current_period_end ?? undefined,
        subscriptionId: status.subscription_id,
        trialDaysRemaining: status.trial_days_remaining ?? undefined,
        plusPromoOffer: status.plus_promo_offer
          ? {
              expiresAt: status.plus_promo_offer.expires_at,
              trialDays: status.plus_promo_offer.trial_days,
            }
          : null,
        powerUpgradeOffer: status.power_upgrade_offer
          ? { expiresAt: status.power_upgrade_offer.expires_at }
          : null,
        paymentMethod: status.payment_method,
        endedInTrial: status.ended_in_trial ?? false,
        meeting_limits: status.meeting_limits,
        researchLimits: status.research_limits,
        journalLimits: status.journal_limits,
        routinesLimits: status.routines_limits,
        hasAppliedReferral: status.has_applied_referral,
        isFree: status.is_free ?? false,
        referralStatus: status.referral_status,
        referralRewardType: status.referral_reward_type ?? null,
        userReferralRewardType: status.user_referral_reward_type ?? null,
        accountCreditCents: status.account_credit_cents,
        creditBalance: status.credit_balance?.purchased_balance ?? 0,
        creditTotal: status.credit_balance?.total ?? 0,
        teamPoolAvailable: status.credit_balance?.team_pool_available ?? 0,
        lowBalanceThreshold: status.credit_balance?.low_balance_threshold ?? 0,
        liteModeActive: status.credit_balance?.lite_mode_active ?? false,
        creditLimits: status.credit_limits,
        autoRefill: status.auto_refill ?? { enabled: false },
        team: status.team ?? null,
        isTeamMember: status.is_team_member ?? false,

        isLoading: false,
        error: null,
        lastUpdated: new Date().toISOString(),
      });
      syncSubscriptionPlanToContextKit(status.current_plan);
    } catch (error) {
      console.error('Failed to get subscription status:', error);
      subscriptionStore.updateState({
        isLoading: false,
        error: (error as Error).message,
      });
    }
  },

  /** Fetches pricing plans from the backend API and updates the store. */
  async getPricingPlans() {
    subscriptionStore.updateState({
      pricingPlansLoading: true,
      pricingPlansError: null,
    });
    try {
      const response = await backendApis.subscription.getPricingPlans();
      subscriptionStore.updateState({
        pricingPlans: response.plans,
        pricingPlansLoading: false,
        pricingPlansError: null,
      });
    } catch (error) {
      console.error('Failed to get pricing plans:', error);
      subscriptionStore.updateState({
        pricingPlansLoading: false,
        pricingPlansError: (error as Error).message,
      });
    }
  },

  /** Creates a Stripe checkout session and redirects the user. */
  async createCheckoutSession(request: CreateCheckoutSessionRequest) {
    if (isStripeRedirectBlocked()) return;
    subscriptionStore.setCheckoutLoading(true);
    try {
      const data = await backendApis.subscription.createCheckoutSession({
        ...request,
        return_channel: readCheckoutReturnChannel(),
        client: checkoutClient(),
        return_to:
          request.return_to ??
          (window.location.pathname.startsWith('/channels')
            ? 'channels'
            : undefined),
      });
      openStripe(
        data.url,
        'Redirecting to checkout...',
        'Finish checkout in your browser'
      );
      if (inElectron()) subscriptionStore.setCheckoutLoading(false);
    } catch (error) {
      const detail = error instanceof HttpError ? error.body?.detail : null;
      toast.error(
        typeof detail === 'string'
          ? detail
          : 'Failed to create checkout session.'
      );
      subscriptionStore.setCheckoutLoading(false);
    }
  },

  /** Resolves unless checkout is redirecting this page to Stripe. */
  async startPlanCheckout(
    plan: string | undefined,
    returnTo: CreateCheckoutSessionRequest['return_to']
  ) {
    await Promise.all([
      subscriptionApis.getStatus(),
      subscriptionApis.getPricingPlans(),
    ]);
    const hasSubscription =
      subscriptionStore.isActive ||
      subscriptionStore.isTeamMember ||
      subscriptionStore.state.subscriptionStatus === 'past_due';
    const pricingPlan = subscriptionStore.state.pricingPlans.find(
      (p) => p.id === plan
    );
    if (subscriptionStore.state.error || hasSubscription || !pricingPlan) {
      return;
    }
    await subscriptionApis.createCheckoutSession({
      price_id: pricingPlan.stripe_price_id,
      return_to: returnTo,
    });
    if (subscriptionStore.checkoutLoading) await new Promise(() => {});
  },

  /** Cancels the current subscription. */
  async cancelSubscription() {
    subscriptionStore.setCancellingLoading(true);
    try {
      await backendApis.subscription.cancelSubscription();
      toast.success('Your subscription is scheduled for cancellation.');
      await subscriptionApis.getStatus();
    } catch (_error) {
      toast.error('Failed to cancel subscription. Please contact support.');
    } finally {
      subscriptionStore.setCancellingLoading(false);
    }
  },

  /** Resumes a subscription scheduled for cancellation. */
  async resumeSubscription() {
    subscriptionStore.setResumingLoading(true);
    try {
      await backendApis.subscription.resumeSubscription();
      toast.success('Your subscription has been reactivated.');
      await subscriptionApis.getStatus();
    } catch (_error) {
      toast.error('Failed to reactivate subscription. Please contact support.');
    } finally {
      subscriptionStore.setResumingLoading(false);
    }
  },

  async createPortalSession(options?: {
    is_upgrade?: boolean;
    add_card?: boolean;
  }) {
    if (isStripeRedirectBlocked()) return;
    if (options?.is_upgrade) {
      subscriptionStore.setUpgradeLoading(true);
    } else {
      subscriptionStore.setPortalLoading(true);
    }
    try {
      const data = await backendApis.subscription.createPortalSession({
        return_channel: readCheckoutReturnChannel(),
        client: checkoutClient(),
        ...(options?.add_card ? { add_card: true } : {}),
      });
      openStripe(
        data.url,
        'Redirecting to subscription management...',
        'Opening billing in your browser'
      );
    } catch (error) {
      const message =
        (error as any)?.response?.data?.message ||
        'Failed to open subscription management.';
      toast.error(message);
    } finally {
      if (options?.is_upgrade) {
        subscriptionStore.setUpgradeLoading(false);
      } else {
        subscriptionStore.setPortalLoading(false);
      }
    }
  },

  /** Gets cancellation context including retention offer eligibility. */
  async getCancellationContext() {
    return backendApis.subscription.getCancellationContext();
  },

  /** Applies retention offer */
  async applyRetentionOffer() {
    const result = await backendApis.subscription.applyRetentionOffer();
    if (result.success) {
      toast.success('Discount applied! Your subscription will continue.');
      await subscriptionApis.getStatus();
    }
    return result;
  },

  async getInvoices() {
    try {
      const result = await backendApis.subscription.getInvoices();
      return result.invoices;
    } catch (_error) {
      toast.error('Failed to fetch invoices');
      return [];
    }
  },

  async applyPromoCode(code: string) {
    try {
      const result = await backendApis.subscription.applyPromoCode(code);
      if (result.success) {
        toast.success(`Promo code applied: ${result.discount_description}`);
        await subscriptionApis.getStatus();
      }
      return result;
    } catch (error) {
      const message =
        (error as any)?.response?.data?.detail ||
        'Failed to apply promo code. Please check the code and try again.';
      toast.error(message);
      return null;
    }
  },

  async upgradeToPro(request: UpgradeToProRequest) {
    try {
      const result = await backendApis.subscription.upgradeToPro(request);
      if (result.success) {
        toast.success('Upgraded to Pro!');
        await subscriptionApis.getStatus();
      }
      return result;
    } catch (error) {
      const message =
        (error as any)?.response?.data?.detail ||
        'Failed to upgrade. Please try again.';
      toast.error(message);
      return null;
    }
  },

  async upgradeToPower(request: UpgradeToPowerRequest) {
    try {
      const result = await backendApis.subscription.upgradeToPower(request);
      if (result.success) {
        toast.success('Upgraded to Power!');
        await subscriptionApis.getStatus();
      }
      return result;
    } catch (error) {
      const detail = error instanceof HttpError ? error.body?.detail : null;
      toast.error(
        typeof detail === 'string'
          ? detail
          : 'Failed to upgrade. Please try again.'
      );
      return null;
    }
  },

  async getCreditPacks() {
    return backendApis.subscription.getCreditPacks();
  },

  async getCreditBalance() {
    return backendApis.subscription.getCreditBalance();
  },

  async purchaseCreditPack(
    request: PurchaseCreditPackRequest,
    rateCentsPer1000?: number,
    options?: { teamId?: string }
  ) {
    try {
      const result = options?.teamId
        ? await backendApis.subscription.purchaseTeamCreditPack(
            options.teamId,
            request
          )
        : await backendApis.subscription.purchaseCreditPack(request);
      if (result.status === 'succeeded') {
        toast.success(
          result.new_balance != null && rateCentsPer1000 != null
            ? `Usage added. Balance: ${formatCentsExact(
                creditsToValueCents(result.new_balance, rateCentsPer1000)
              )}`
            : 'Usage added.'
        );
        await Promise.all([
          options?.teamId
            ? queryClient.invalidateQueries({ queryKey: ['team-wallet'] })
            : subscriptionApis.getStatus(),
          queryClient.invalidateQueries({ queryKey: ['credit-packs'] }),
          queryClient.invalidateQueries({ queryKey: ['credit-balance'] }),
        ]);
      } else if (result.status === 'requires_action' && result.checkout_url) {
        openStripe(
          result.checkout_url,
          'Redirecting to checkout...',
          'Finish checkout in your browser'
        );
      } else {
        toast.error(result.message || 'Payment failed. Please try again.');
      }
      return result;
    } catch (error) {
      const message =
        (error as any)?.response?.data?.detail ||
        'Failed to add usage. Please try again.';
      toast.error(message);
      return null;
    }
  },

  async updateAutoRefill(request: UpdateAutoRefillRequest) {
    try {
      const result = await backendApis.subscription.updateAutoRefill(request);
      subscriptionStore.updateState({ autoRefill: result });
      toast.success(
        request.enabled ? 'Auto-reload is on.' : 'Auto-reload turned off.'
      );
      return result;
    } catch (error) {
      const message =
        (error as any)?.response?.data?.detail ||
        'Failed to update auto-reload. Please try again.';
      toast.error(message);
      return null;
    }
  },
};

export const useCreditBalanceQuery = () =>
  useQuery({
    queryKey: ['credit-balance'],
    queryFn: subscriptionApis.getCreditBalance,
  });

export function useSubscription() {
  return {
    status: {
      current_plan: subscriptionStore.state.currentPlan,
      planChangeDate: subscriptionStore.state.planChangeDate,
      stripe_customer_id: subscriptionStore.state.stripeCustomerId,
      subscription_status: subscriptionStore.state.subscriptionStatus,
      current_period_end: subscriptionStore.state.currentPeriodEnd,
      subscription_id: subscriptionStore.state.subscriptionId,
      trial_days_remaining: subscriptionStore.state.trialDaysRemaining,
      payment_method: subscriptionStore.state.paymentMethod,
      meeting_limits: subscriptionStore.state.meeting_limits,
      research_limits: subscriptionStore.state.researchLimits,
      journal_limits: subscriptionStore.state.journalLimits,
      routines_limits: subscriptionStore.state.routinesLimits,
      has_applied_referral: subscriptionStore.state.hasAppliedReferral,
      referral_status: subscriptionStore.state.referralStatus,
      referral_reward_type: subscriptionStore.state.referralRewardType,
      user_referral_reward_type: subscriptionStore.state.userReferralRewardType,
    },
    hasReachedMeetingLimit: subscriptionStore.hasReachedMeetingLimit,
    isLoading: subscriptionStore.state.isLoading,
    isError: !!subscriptionStore.state.error,
    error: subscriptionStore.state.error
      ? new Error(subscriptionStore.state.error)
      : null,
    pricingPlans: subscriptionStore.state.pricingPlans,
    pricingPlansLoading: subscriptionStore.state.pricingPlansLoading,
    pricingPlansError: subscriptionStore.state.pricingPlansError,
    createCheckoutSession: subscriptionApis.createCheckoutSession,
    isCreatingCheckout: subscriptionStore.checkoutLoading,
    cancelSubscription: subscriptionApis.cancelSubscription,
    isCancelling: subscriptionStore.cancellingLoading,
    resumeSubscription: subscriptionApis.resumeSubscription,
    isResuming: subscriptionStore.resumingLoading,
    createPortalSession: subscriptionApis.createPortalSession,
    isCreatingPortal: subscriptionStore.portalLoading,
    isCreatingUpgrade: subscriptionStore.upgradeLoading,
    getCancellationContext: subscriptionApis.getCancellationContext,
    applyRetentionOffer: subscriptionApis.applyRetentionOffer,
    upgradeToPro: subscriptionApis.upgradeToPro,
    refetch: subscriptionApis.getStatus,
    fetchPricingPlans: subscriptionApis.getPricingPlans,
  };
}
